import React from 'react'
import { countLabel } from '@/utils/plural'
import {
	AvatarCircle,
	avatarColorForIndex,
	AvatarImg,
	AvatarRow,
	FriendsCaption,
	MoreBubble,
	StackRoot,
} from './AvatarStack.styles'
import { OverviewTraveler } from './core'

interface AvatarStackProps {
	travelers: OverviewTraveler[]
	/** Total member count (may exceed `travelers.length` → renders a "+N" bubble). */
	memberCount: number
	size?: number
	max?: number
	/** Show the "N friends" caption beside the avatars. */
	showNames?: boolean
	/** When provided, the whole stack becomes a button (e.g. navigates to members). */
	onClick?: () => void
	ariaLabel?: string
}

/** Overlapping avatar stack with white rings and a "+N" overflow bubble. */
const AvatarStack: React.FC<AvatarStackProps> = ({
	travelers,
	memberCount,
	size = 34,
	max = 4,
	showNames = false,
	onClick,
	ariaLabel,
}) => {
	const interactive = typeof onClick === 'function'
	const visible = travelers.slice(0, max)
	const overflow = memberCount - visible.length
	const label = ariaLabel ?? `View ${memberCount} member${memberCount === 1 ? '' : 's'}`

	return (
		<StackRoot
			as={interactive ? 'button' : 'span'}
			$gap={showNames ? 10 : 0}
			$interactive={interactive}
			{...(interactive ? { type: 'button' as const, onClick, 'aria-label': label } : {})}
		>
			<AvatarRow>
				{visible.map((traveler, index) => (
					<AvatarCircle key={traveler.id} $size={size} $index={index} $bg={avatarColorForIndex(index)}>
						{traveler.avatarUrl ? <AvatarImg src={traveler.avatarUrl} alt="" /> : traveler.initials}
					</AvatarCircle>
				))}
				{overflow > 0 && (
					<MoreBubble $size={size} $index={visible.length} $bg="transparent">
						+{overflow}
					</MoreBubble>
				)}
			</AvatarRow>
			{showNames && memberCount > 0 && (
				<FriendsCaption>
					{countLabel(memberCount, 'friend')}
				</FriendsCaption>
			)}
		</StackRoot>
	)
}

export default AvatarStack
